import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarClock, Clock, AlertCircle, AlertTriangle, CheckCircle2, Circle,
  ArrowRight, ChevronDown, Plus, X, Trash2, Edit3, Timer, Slash,
  Calendar, Zap, LayoutList, GitCommitVertical, FileText, Shield,
  DollarSign, Users, Gavel, PenTool, MoreHorizontal, RotateCcw
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Deadline {
  _id?: string;
  key: string;
  label: string;
  description: string | null;
  category: "probate" | "tax" | "creditor" | "distribution" | "drafting" | "other";
  dueDate: string;
  originalDueDate: string | null;
  status: "upcoming" | "due_soon" | "overdue" | "completed" | "extended" | "not_applicable";
  completedAt: string | null;
  completedBy: string | null;
  completionProof: string | null;
  completionDocumentId: string | null;
  extensionDate: string | null;
  extensionReason: string | null;
  linkedTaskIds: string[];
  jurisdiction: string | null;
  autoCalculated: boolean;
  notificationDays: number[];
  notes: string | null;
}

interface Props {
  matterId: string;
  caseData: any;
  onUpdate: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const CATEGORY_CONFIGS: Record<string, { label: string; plural: string; Icon: any; color: string; bgColor: string; badgeColor: string; borderColor: string }> = {
  probate:       { label: "Probate",       plural: "Probate Deadlines",       Icon: Gavel,           color: "text-indigo-600",  bgColor: "bg-indigo-50",  badgeColor: "bg-indigo-100 text-indigo-700",  borderColor: "border-l-indigo-400" },
  tax:           { label: "Tax",           plural: "Tax Deadlines",           Icon: DollarSign,      color: "text-amber-600",   bgColor: "bg-amber-50",   badgeColor: "bg-amber-100 text-amber-700",    borderColor: "border-l-amber-400" },
  creditor:      { label: "Creditor",      plural: "Creditor Deadlines",      Icon: Shield,          color: "text-red-600",     bgColor: "bg-red-50",     badgeColor: "bg-red-100 text-red-700",        borderColor: "border-l-red-400" },
  distribution:  { label: "Distribution",  plural: "Distribution Deadlines",  Icon: Users,           color: "text-emerald-600", bgColor: "bg-emerald-50", badgeColor: "bg-emerald-100 text-emerald-700", borderColor: "border-l-emerald-400" },
  drafting:      { label: "Drafting",      plural: "Drafting Deadlines",      Icon: PenTool,         color: "text-blue-600",    bgColor: "bg-blue-50",    badgeColor: "bg-blue-100 text-blue-700",      borderColor: "border-l-blue-400" },
  other:         { label: "Other",         plural: "Other Deadlines",         Icon: MoreHorizontal,  color: "text-slate-600",   bgColor: "bg-slate-100",  badgeColor: "bg-slate-200 text-slate-700",    borderColor: "border-l-slate-400" },
};

const STATUS_CONFIGS: Record<string, { label: string; Icon: any; color: string; dotColor: string }> = {
  upcoming:       { label: "Upcoming",       Icon: Circle,         color: "text-blue-600",    dotColor: "bg-blue-500" },
  due_soon:       { label: "Due Soon",       Icon: AlertTriangle,  color: "text-amber-600",   dotColor: "bg-amber-500" },
  overdue:        { label: "Overdue",        Icon: AlertCircle,    color: "text-red-600",     dotColor: "bg-red-500" },
  completed:      { label: "Completed",      Icon: CheckCircle2,   color: "text-emerald-600", dotColor: "bg-emerald-500" },
  extended:       { label: "Extended",       Icon: ArrowRight,     color: "text-purple-600",  dotColor: "bg-purple-500" },
  not_applicable: { label: "N/A",            Icon: Slash,          color: "text-slate-400",   dotColor: "bg-slate-400" },
};

const JURISDICTION_OPTIONS = [
  { value: "ontario", label: "Ontario" },
  { value: "british_columbia", label: "British Columbia" },
  { value: "alberta", label: "Alberta" },
  { value: "quebec", label: "Quebec" },
  { value: "other", label: "Other" },
];

const CATEGORY_OPTIONS = [
  { value: "probate", label: "Probate" },
  { value: "tax", label: "Tax" },
  { value: "creditor", label: "Creditor" },
  { value: "distribution", label: "Distribution" },
  { value: "drafting", label: "Drafting" },
  { value: "other", label: "Other" },
];

const NOTIFICATION_DAY_OPTIONS = [7, 14, 30, 60, 90];

const COMMON_DEADLINES: { category: string; items: { key: string; label: string; description: string; daysFromDeath: number | null; notes: string }[] }[] = [
  {
    category: "Probate",
    items: [
      { key: "apply_certificate_appointment", label: "Apply for Certificate of Appointment", description: "File application with the court for authority to administer the estate", daysFromDeath: 30, notes: "" },
      { key: "file_notice_estate_registrar", label: "File Notice to Estate Registrar", description: "Notify the Estate Registrar of the court filing", daysFromDeath: 30, notes: "" },
      { key: "publish_notice_creditors", label: "Publish Notice to Creditors", description: "Publish formal notice in local newspaper/Ontario Gazette", daysFromDeath: 0, notes: "Publish immediately" },
      { key: "creditor_claims_period", label: "Creditor Claims Period Expires", description: "Period during which creditors may file claims against the estate (ON: ~210 days from notice)", daysFromDeath: 210, notes: "Varies by jurisdiction" },
    ],
  },
  {
    category: "Tax",
    items: [
      { key: "file_final_t1", label: "File Final T1 Tax Return (up to date of death)", description: "Personal income tax return for Jan 1 to date of death", daysFromDeath: 180, notes: "April 30 or 6 months from death, whichever is later" },
      { key: "file_rights_things", label: "File Rights or Things Return (optional)", description: "Optional return for income rights at date of death", daysFromDeath: 365, notes: "1 year from date of death" },
      { key: "file_t3_trust", label: "File T3 Trust Return (estate income)", description: "Report estate income earned after date of death", daysFromDeath: 455, notes: "90 days after fiscal year-end of estate" },
      { key: "request_clearance_certificate", label: "Request Clearance Certificate (CRA)", description: "Obtain CRA clearance before final distribution", daysFromDeath: null, notes: "Before final distribution" },
    ],
  },
  {
    category: "Distribution",
    items: [
      { key: "interim_distribution", label: "Interim Distribution to Beneficiaries", description: "Initial partial distribution after creditor period closes", daysFromDeath: 240, notes: "After creditor claims period" },
      { key: "final_distribution", label: "Final Distribution & Accounting", description: "Complete final accounting and distribute remaining estate assets", daysFromDeath: 365, notes: "1 year target (executor's year)" },
      { key: "pass_accounts", label: "Pass Accounts (if required)", description: "Court-supervised review of estate accounts", daysFromDeath: null, notes: "As needed" },
    ],
  },
  {
    category: "Drafting",
    items: [
      { key: "initial_client_interview", label: "Complete initial client interview", description: "Gather all necessary information for will and estate plan drafting", daysFromDeath: 7, notes: "" },
      { key: "first_draft_review", label: "First draft to client for review", description: "Complete and deliver initial draft of estate planning documents", daysFromDeath: 14, notes: "" },
      { key: "client_revisions", label: "Client review & revisions", description: "Incorporate client feedback and finalize documents", daysFromDeath: 28, notes: "" },
      { key: "schedule_signing", label: "Schedule signing ceremony", description: "Arrange execution ceremony with witnesses and notary", daysFromDeath: 35, notes: "" },
    ],
  },
];

const emptyDeadline: Omit<Deadline, "_id"> = {
  key: "", label: "", description: null,
  category: "other", dueDate: "", originalDueDate: null,
  status: "upcoming", completedAt: null, completedBy: null,
  completionProof: null, completionDocumentId: null,
  extensionDate: null, extensionReason: null,
  linkedTaskIds: [], jurisdiction: null,
  autoCalculated: false, notificationDays: [7, 30],
  notes: null,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const toDateStr = (d: string | Date | null): string => {
  if (!d) return "";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const formatDate = (d: string | Date | null): string => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
};

const daysDiff = (dateStr: string | Date | null): number => {
  if (!dateStr) return 0;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/(^_|_$)/g, "");

// ── Component ─────────────────────────────────────────────────────────────────
const EstateDeadlinesTab: React.FC<Props> = ({ matterId, caseData, onUpdate }) => {
  const rawDeadlines: Deadline[] = caseData?.deadlines || [];

  // Auto-calculate statuses
  const deadlines = useMemo(() => {
    return rawDeadlines.map(d => {
      if (d.status === "completed" || d.status === "not_applicable" || d.status === "extended") return d;
      const diff = daysDiff(d.dueDate);
      let status = d.status;
      if (diff < 0) status = "overdue";
      else if (diff <= 30) status = "due_soon";
      else status = "upcoming";
      return { ...d, status };
    });
  }, [rawDeadlines]);

  // State
  const [activeCategory, setActiveCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState<"active" | "completed" | "all">("active");
  const [sortBy, setSortBy] = useState<"dueDate" | "category" | "status">("dueDate");
  const [viewMode, setViewMode] = useState<"cards" | "timeline">("cards");
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    Object.keys(CATEGORY_CONFIGS).reduce((acc, k) => ({ ...acc, [k]: true }), {})
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState<number | null>(null);
  const [showExtendModal, setShowExtendModal] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [form, setForm] = useState<Omit<Deadline, "_id">>({ ...emptyDeadline });
  const [completeForm, setCompleteForm] = useState({ completedAt: new Date().toISOString().split("T")[0], completionProof: "", notes: "" });
  const [extendForm, setExtendForm] = useState({ extensionDate: "", extensionReason: "" });
  const [generateForm, setGenerateForm] = useState({ dateOfDeath: "", jurisdiction: "ontario", selected: {} as Record<string, boolean> });

  // Stats
  const stats = useMemo(() => {
    const s = { overdue: 0, due_soon: 0, upcoming: 0, completed: 0 };
    deadlines.forEach(d => {
      if (d.status === "overdue") s.overdue++;
      else if (d.status === "due_soon") s.due_soon++;
      else if (d.status === "upcoming" || d.status === "extended") s.upcoming++;
      else if (d.status === "completed") s.completed++;
    });
    return s;
  }, [deadlines]);

  // Filtered + sorted
  const filtered = useMemo(() => {
    let items = deadlines.map((d, i) => ({ deadline: d, idx: i }));

    if (activeCategory !== "all") {
      items = items.filter(({ deadline }) => deadline.category === activeCategory);
    }

    if (statusFilter === "active") {
      items = items.filter(({ deadline }) => !["completed", "not_applicable"].includes(deadline.status));
    } else if (statusFilter === "completed") {
      items = items.filter(({ deadline }) => deadline.status === "completed");
    }

    items.sort((a, b) => {
      if (sortBy === "dueDate") {
        return new Date(a.deadline.dueDate).getTime() - new Date(b.deadline.dueDate).getTime();
      }
      if (sortBy === "category") {
        return a.deadline.category.localeCompare(b.deadline.category);
      }
      const statusOrder = { overdue: 0, due_soon: 1, upcoming: 2, extended: 3, completed: 4, not_applicable: 5 };
      return (statusOrder[a.deadline.status] || 5) - (statusOrder[b.deadline.status] || 5);
    });

    return items;
  }, [deadlines, activeCategory, statusFilter, sortBy]);

  // Grouped by category
  const grouped = useMemo(() => {
    const g: Record<string, { deadline: Deadline; idx: number }[]> = {};
    Object.keys(CATEGORY_CONFIGS).forEach(c => { g[c] = []; });
    filtered.forEach(item => {
      if (g[item.deadline.category]) g[item.deadline.category].push(item);
    });
    return g;
  }, [filtered]);

  const toggleSection = (cat: string) => setExpandedSections(prev => ({ ...prev, [cat]: !prev[cat] }));

  // ── API Handlers ──────────────────────────────────────────────────────────
  const openAddModal = () => {
    setForm({ ...emptyDeadline, dueDate: new Date().toISOString().split("T")[0] });
    setEditingIdx(null);
    setShowAddModal(true);
  };

  const openEditModal = (deadline: Deadline, idx: number) => {
    setForm({
      ...deadline,
      dueDate: toDateStr(deadline.dueDate),
      originalDueDate: toDateStr(deadline.originalDueDate),
      extensionDate: toDateStr(deadline.extensionDate),
      completedAt: toDateStr(deadline.completedAt),
    });
    setEditingIdx(idx);
    setShowAddModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const payload = { ...form, key: form.key || slugify(form.label) };
      if (editingIdx !== null) {
        await estatesApi.updateSubdoc(caseData._id, "deadlines", editingIdx, payload);
      } else {
        await estatesApi.addSubdoc(caseData._id, "deadlines", payload);
      }
      onUpdate();
      setShowAddModal(false);
    } catch (err) {
      console.error("Failed to save deadline:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (showCompleteModal === null) return;
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const deadline = deadlines[showCompleteModal];
      await estatesApi.updateSubdoc(caseData._id, "deadlines", showCompleteModal, {
        ...deadline,
        status: "completed",
        completedAt: completeForm.completedAt,
        completionProof: completeForm.completionProof || null,
        notes: completeForm.notes || deadline.notes,
      });
      onUpdate();
      setShowCompleteModal(null);
    } catch (err) {
      console.error("Failed to complete deadline:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleExtend = async () => {
    if (showExtendModal === null) return;
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const deadline = deadlines[showExtendModal];
      await estatesApi.updateSubdoc(caseData._id, "deadlines", showExtendModal, {
        ...deadline,
        status: "extended",
        originalDueDate: deadline.originalDueDate || deadline.dueDate,
        dueDate: extendForm.extensionDate,
        extensionDate: extendForm.extensionDate,
        extensionReason: extendForm.extensionReason,
      });
      onUpdate();
      setShowExtendModal(null);
    } catch (err) {
      console.error("Failed to extend deadline:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (idx: number) => {
    try {
      const { estatesApi } = await import("../../services/api");
      await estatesApi.removeSubdoc(caseData._id, "deadlines", idx);
      onUpdate();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error("Failed to delete deadline:", err);
    }
  };

  const handleGenerateDeadlines = async () => {
    if (!generateForm.dateOfDeath) return;
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const selectedItems = COMMON_DEADLINES.flatMap(cat =>
        cat.items.filter(item => generateForm.selected[item.key])
      );

      for (const item of selectedItems) {
        const dueDate = item.daysFromDeath !== null
          ? addDays(generateForm.dateOfDeath, item.daysFromDeath)
          : "";
        const newDeadline = {
          ...emptyDeadline,
          key: item.key,
          label: item.label,
          description: item.description,
          category: COMMON_DEADLINES.find(c => c.items.includes(item))?.category.toLowerCase() as any || "other",
          dueDate,
          jurisdiction: generateForm.jurisdiction,
          autoCalculated: true,
          notes: item.notes || null,
        };
        await estatesApi.addSubdoc(caseData._id, "deadlines", newDeadline);
      }

      onUpdate();
      setShowGenerateModal(false);
      setGenerateForm({ dateOfDeath: "", jurisdiction: "ontario", selected: {} });
    } catch (err) {
      console.error("Failed to generate deadlines:", err);
    } finally {
      setSaving(false);
    }
  };

  const openCompleteModal = (idx: number) => {
    setCompleteForm({ completedAt: new Date().toISOString().split("T")[0], completionProof: "", notes: "" });
    setShowCompleteModal(idx);
  };

  const openExtendModal = (idx: number) => {
    const d = deadlines[idx];
    setExtendForm({ extensionDate: toDateStr(d.dueDate), extensionReason: "" });
    setShowExtendModal(idx);
  };

  const toggleNotificationDay = (day: number) => {
    setForm(prev => ({
      ...prev,
      notificationDays: prev.notificationDays.includes(day)
        ? prev.notificationDays.filter(d => d !== day)
        : [...prev.notificationDays, day].sort((a, b) => a - b),
    }));
  };

  const toggleGenerateItem = (key: string) => {
    setGenerateForm(prev => ({
      ...prev,
      selected: { ...prev.selected, [key]: !prev.selected[key] },
    }));
  };

  const toggleAllInCategory = (category: string) => {
    const catItems = COMMON_DEADLINES.find(c => c.category === category)?.items || [];
    const allSelected = catItems.every(item => generateForm.selected[item.key]);
    setGenerateForm(prev => {
      const newSelected = { ...prev.selected };
      catItems.forEach(item => { newSelected[item.key] = !allSelected; });
      return { ...prev, selected: newSelected };
    });
  };

  const getRelativeText = (deadline: Deadline): { text: string; className: string } => {
    if (deadline.status === "completed") return { text: `Completed ${formatDate(deadline.completedAt)}`, className: "text-emerald-600" };
    if (deadline.status === "not_applicable") return { text: "Not applicable", className: "text-slate-400" };
    const diff = daysDiff(deadline.dueDate);
    if (diff < 0) return { text: `${Math.abs(diff)} day${Math.abs(diff) !== 1 ? "s" : ""} overdue`, className: "text-red-600 font-semibold" };
    if (diff === 0) return { text: "Due today", className: "text-amber-600 font-semibold" };
    if (diff === 1) return { text: "Due tomorrow", className: "text-amber-600" };
    return { text: `${diff} day${diff !== 1 ? "s" : ""} remaining`, className: diff <= 30 ? "text-amber-600" : "text-blue-600" };
  };

  const selectedGenerateCount = Object.values(generateForm.selected).filter(Boolean).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-rose-50 flex items-center justify-center">
              <CalendarClock className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-800">Estate Deadlines</h2>
                {stats.overdue > 0 && (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                  </span>
                )}
                {stats.overdue === 0 && stats.due_soon > 0 && (
                  <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
                )}
              </div>
              <p className="text-sm text-slate-500">
                {deadlines.length} {deadlines.length === 1 ? "deadline" : "deadlines"}
                {stats.overdue > 0 && <span className="text-red-600 font-medium">, {stats.overdue} overdue</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setGenerateForm({ dateOfDeath: "", jurisdiction: "ontario", selected: {} });
                setShowGenerateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              <Zap className="h-4 w-4" /> Generate Common
            </button>
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Deadline
            </button>
          </div>
        </div>

        {/* Summary Stats Row */}
        {deadlines.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-slate-100 p-3 text-center">
              <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Overdue</p>
              <p className="text-2xl font-bold mt-1 text-red-600">{stats.overdue}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-3 text-center">
              <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Due Soon</p>
              <p className="text-2xl font-bold mt-1 text-amber-600">{stats.due_soon}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-3 text-center">
              <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Upcoming</p>
              <p className="text-2xl font-bold mt-1 text-blue-600">{stats.upcoming}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-3 text-center">
              <p className="text-xs text-slate-400 uppercase font-semibold tracking-wide">Completed</p>
              <p className="text-2xl font-bold mt-1 text-emerald-600">{stats.completed}</p>
            </div>
          </div>
        )}
      </div>

      {/* Filter / Sort Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide flex-1">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === "all" ? "bg-[#0F5132] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            All ({deadlines.length})
          </button>
          {Object.entries(CATEGORY_CONFIGS).map(([key, cfg]) => {
            const count = deadlines.filter(d => d.category === key).length;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === key ? "bg-[#0F5132] text-white" : count > 0 ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-slate-50 text-slate-400"
                }`}
              >
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Status + Sort + View toggles */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            {(["active", "completed", "all"] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === s ? "bg-[#0F5132] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {s === "active" ? "Active" : s === "completed" ? "Done" : "All"}
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value as any)}
            className="rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="dueDate">Sort: Due Date</option>
            <option value="category">Sort: Category</option>
            <option value="status">Sort: Status</option>
          </select>
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            <button
              onClick={() => setViewMode("cards")}
              className={`p-1.5 transition-colors ${viewMode === "cards" ? "bg-[#0F5132] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Card view"
            >
              <LayoutList className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("timeline")}
              className={`p-1.5 transition-colors ${viewMode === "timeline" ? "bg-[#0F5132] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Timeline view"
            >
              <GitCommitVertical className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {deadlines.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <div className="h-16 w-16 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-4">
            <CalendarClock className="h-8 w-8 text-rose-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Deadlines Set</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
            Track important estate dates -- probate filings, tax returns, creditor periods, and distribution targets.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => {
                setGenerateForm({ dateOfDeath: "", jurisdiction: "ontario", selected: {} });
                setShowGenerateModal(true);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              <Zap className="h-4 w-4" /> Generate Common Deadlines
            </button>
            <button
              onClick={openAddModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors"
            >
              <Plus className="h-4 w-4" /> Add First Deadline
            </button>
          </div>
        </motion.div>
      )}

      {/* Card View */}
      {viewMode === "cards" && deadlines.length > 0 && (
        <AnimatePresence>
          {(sortBy === "category" || activeCategory === "all" ? Object.keys(CATEGORY_CONFIGS) : [activeCategory]).map(cat => {
            const cfg = CATEGORY_CONFIGS[cat];
            const items = grouped[cat] || [];
            if (items.length === 0) return null;
            const CatIcon = cfg.Icon;
            const isOpen = expandedSections[cat];

            return (
              <motion.div
                key={cat}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-l-4 ${cfg.borderColor}`}
              >
                <button
                  onClick={() => toggleSection(cat)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-xl ${cfg.bgColor} flex items-center justify-center`}>
                      <CatIcon className={`h-4.5 w-4.5 ${cfg.color}`} />
                    </div>
                    <h3 className="font-semibold text-slate-800">{cfg.plural}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.badgeColor}`}>{items.length}</span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 space-y-2">
                        {items.map(({ deadline: d, idx }, i) => {
                          const statusCfg = STATUS_CONFIGS[d.status] || STATUS_CONFIGS.upcoming;
                          const StatusIcon = statusCfg.Icon;
                          const relative = getRelativeText(d);

                          return (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.03 }}
                              className="group flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all bg-slate-50/30"
                            >
                              {/* Status Icon */}
                              <div className="flex-shrink-0 mt-0.5">
                                {d.status === "overdue" ? (
                                  <span className="relative flex h-6 w-6 items-center justify-center">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-200 opacity-50" />
                                    <AlertCircle className="relative h-5 w-5 text-red-600" />
                                  </span>
                                ) : (
                                  <StatusIcon className={`h-5 w-5 ${statusCfg.color}`} />
                                )}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-slate-800">{d.label}</span>
                                  {d.jurisdiction && (
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">
                                      {JURISDICTION_OPTIONS.find(j => j.value === d.jurisdiction)?.label || d.jurisdiction}
                                    </span>
                                  )}
                                  {d.autoCalculated && (
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-600">
                                      <Zap className="h-2.5 w-2.5 inline mr-0.5" />Auto
                                    </span>
                                  )}
                                </div>
                                {d.description && (
                                  <p className="text-xs text-slate-500 mt-0.5">{d.description}</p>
                                )}
                                <div className="flex items-center gap-3 mt-1.5 text-xs flex-wrap">
                                  <span className="flex items-center gap-1 text-slate-500">
                                    <Calendar className="h-3 w-3" />
                                    {d.status === "extended" && d.originalDueDate ? (
                                      <>
                                        <span className="line-through text-slate-400">{formatDate(d.originalDueDate)}</span>
                                        <ArrowRight className="h-3 w-3 text-purple-500" />
                                        <span className="text-purple-600 font-medium">{formatDate(d.dueDate)}</span>
                                      </>
                                    ) : (
                                      formatDate(d.dueDate)
                                    )}
                                  </span>
                                  <span className={relative.className}>{relative.text}</span>
                                </div>
                                {d.status === "extended" && d.extensionReason && (
                                  <p className="text-xs text-purple-600 mt-1 italic">Extension: {d.extensionReason}</p>
                                )}
                                {d.notes && d.status !== "completed" && (
                                  <p className="text-xs text-slate-400 mt-1">{d.notes}</p>
                                )}
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                {d.status !== "completed" && d.status !== "not_applicable" && (
                                  <>
                                    <button
                                      onClick={() => openCompleteModal(idx)}
                                      className="p-1.5 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                                      title="Mark complete"
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => openExtendModal(idx)}
                                      className="p-1.5 rounded-lg hover:bg-purple-50 text-slate-400 hover:text-purple-600 transition-colors"
                                      title="Extend deadline"
                                    >
                                      <Timer className="h-3.5 w-3.5" />
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => openEditModal(d, idx)}
                                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                  title="Edit"
                                >
                                  <Edit3 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => setShowDeleteConfirm(idx)}
                                  className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      )}

      {/* Timeline View */}
      {viewMode === "timeline" && deadlines.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-slate-200" />

            <div className="space-y-4">
              {filtered.map(({ deadline: d, idx }, i) => {
                const statusCfg = STATUS_CONFIGS[d.status] || STATUS_CONFIGS.upcoming;
                const catCfg = CATEGORY_CONFIGS[d.category] || CATEGORY_CONFIGS.other;
                const relative = getRelativeText(d);

                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="group flex items-start gap-4 pl-0"
                  >
                    {/* Dot */}
                    <div className="relative z-10 flex-shrink-0 mt-1">
                      {d.status === "completed" ? (
                        <div className="h-[30px] w-[30px] rounded-full bg-emerald-100 flex items-center justify-center">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        </div>
                      ) : d.status === "overdue" ? (
                        <span className="relative flex h-[30px] w-[30px] items-center justify-center">
                          <span className="animate-ping absolute inline-flex h-[30px] w-[30px] rounded-full bg-red-200 opacity-40" />
                          <span className={`relative inline-flex h-[30px] w-[30px] rounded-full ${statusCfg.dotColor} items-center justify-center`}>
                            <AlertCircle className="h-3.5 w-3.5 text-white" />
                          </span>
                        </span>
                      ) : (
                        <div className={`h-[30px] w-[30px] rounded-full border-2 border-white shadow-sm ${statusCfg.dotColor} flex items-center justify-center`}>
                          <Circle className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Card */}
                    <div className="flex-1 bg-slate-50/50 rounded-xl border border-slate-100 p-3 hover:border-slate-200 hover:shadow-sm transition-all -mt-0.5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800 text-sm">{d.label}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${catCfg.badgeColor}`}>{catCfg.label}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs">
                            <span className="text-slate-500">{formatDate(d.dueDate)}</span>
                            <span className={relative.className}>{relative.text}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          {d.status !== "completed" && d.status !== "not_applicable" && (
                            <button onClick={() => openCompleteModal(idx)} className="p-1 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button onClick={() => openEditModal(d, idx)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => setShowDeleteConfirm(idx)} className="p-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* No results after filtering */}
      {deadlines.length > 0 && filtered.length === 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500">No deadlines match the current filters.</p>
          <button onClick={() => { setActiveCategory("all"); setStatusFilter("all"); }} className="mt-2 text-sm text-[#0F5132] hover:text-[#146C43] font-medium">
            Clear filters
          </button>
        </motion.div>
      )}

      {/* ── Add/Edit Deadline Modal ──────────────────────────────────────────── */}
      <PremiumModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={editingIdx !== null ? "Edit Deadline" : "Add Deadline"}
        subtitle="Set due dates and track important estate milestones"
        icon={CalendarClock}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Label *</label>
            <input
              value={form.label}
              onChange={e => setForm({ ...form, label: e.target.value })}
              placeholder="e.g., File Final T1 Tax Return"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={form.description || ""}
              onChange={e => setForm({ ...form, description: e.target.value || null })}
              rows={2}
              placeholder="Brief description of this deadline..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
              <SearchableSelect
                options={CATEGORY_OPTIONS}
                value={form.category}
                onChange={v => setForm({ ...form, category: (v || "other") as any })}
                placeholder="Select category"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date *</label>
              <input
                type="date"
                value={toDateStr(form.dueDate)}
                onChange={e => setForm({ ...form, dueDate: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Jurisdiction</label>
            <SearchableSelect
              options={JURISDICTION_OPTIONS}
              value={form.jurisdiction}
              onChange={v => setForm({ ...form, jurisdiction: v || null })}
              placeholder="Select jurisdiction"
              clearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Notification Reminders (days before)</label>
            <div className="flex gap-2 flex-wrap">
              {NOTIFICATION_DAY_OPTIONS.map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleNotificationDay(day)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    form.notificationDays.includes(day)
                      ? "bg-[#0F5132] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {day}d
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={form.notes || ""}
              onChange={e => setForm({ ...form, notes: e.target.value || null })}
              rows={2}
              placeholder="Additional notes..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Modal footer */}
          <div className="flex justify-between pt-4 border-t border-slate-100">
            <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!form.label || !form.dueDate || saving}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : editingIdx !== null ? "Save Changes" : "Add Deadline"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Complete Deadline Modal ───────────────────────────────────────────── */}
      <PremiumModal
        open={showCompleteModal !== null}
        onClose={() => setShowCompleteModal(null)}
        title="Complete Deadline"
        subtitle={showCompleteModal !== null ? deadlines[showCompleteModal]?.label : ""}
        icon={CheckCircle2}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Completion Date</label>
            <input
              type="date"
              value={completeForm.completedAt}
              onChange={e => setCompleteForm({ ...completeForm, completedAt: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Completion Proof / Description</label>
            <textarea
              value={completeForm.completionProof}
              onChange={e => setCompleteForm({ ...completeForm, completionProof: e.target.value })}
              rows={2}
              placeholder="e.g., Filed with CRA, confirmation #12345"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={completeForm.notes}
              onChange={e => setCompleteForm({ ...completeForm, notes: e.target.value })}
              rows={2}
              placeholder="Additional notes..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div className="flex justify-between pt-4 border-t border-slate-100">
            <button onClick={() => setShowCompleteModal(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleComplete}
              disabled={!completeForm.completedAt || saving}
              className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Mark Complete"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Extend Deadline Modal ────────────────────────────────────────────── */}
      <PremiumModal
        open={showExtendModal !== null}
        onClose={() => setShowExtendModal(null)}
        title="Extend Deadline"
        subtitle={showExtendModal !== null ? deadlines[showExtendModal]?.label : ""}
        icon={Timer}
        size="md"
      >
        <div className="space-y-4">
          {showExtendModal !== null && (
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl border border-slate-100 text-sm">
              <Clock className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-slate-500">Original due date:</span>
              <span className="font-medium text-slate-700">{formatDate(deadlines[showExtendModal]?.dueDate)}</span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">New Due Date *</label>
            <input
              type="date"
              value={extendForm.extensionDate}
              onChange={e => setExtendForm({ ...extendForm, extensionDate: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Extension Reason *</label>
            <textarea
              value={extendForm.extensionReason}
              onChange={e => setExtendForm({ ...extendForm, extensionReason: e.target.value })}
              rows={3}
              placeholder="Why is this deadline being extended?"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div className="flex justify-between pt-4 border-t border-slate-100">
            <button onClick={() => setShowExtendModal(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleExtend}
              disabled={!extendForm.extensionDate || !extendForm.extensionReason || saving}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Extend Deadline"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Generate Common Deadlines Modal ──────────────────────────────────── */}
      <PremiumModal
        open={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        title="Generate Common Deadlines"
        subtitle="Auto-populate standard estate deadlines calculated from date of death"
        icon={Zap}
        size="xl"
      >
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date of Death *</label>
              <input
                type="date"
                value={generateForm.dateOfDeath}
                onChange={e => setGenerateForm({ ...generateForm, dateOfDeath: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Jurisdiction</label>
              <SearchableSelect
                options={JURISDICTION_OPTIONS}
                value={generateForm.jurisdiction}
                onChange={v => setGenerateForm({ ...generateForm, jurisdiction: v || "ontario" })}
                placeholder="Select jurisdiction"
              />
            </div>
          </div>

          {generateForm.dateOfDeath && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              {COMMON_DEADLINES.map(cat => {
                const catKey = cat.category.toLowerCase();
                const catCfg = CATEGORY_CONFIGS[catKey];
                const allSelected = cat.items.every(item => generateForm.selected[item.key]);
                const someSelected = cat.items.some(item => generateForm.selected[item.key]);

                return (
                  <div key={cat.category} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className={`flex items-center justify-between px-4 py-3 ${catCfg?.bgColor || "bg-slate-50"}`}>
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                            onChange={() => toggleAllInCategory(cat.category)}
                            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className={`text-sm font-semibold ${catCfg?.color || "text-slate-700"}`}>{cat.category}</span>
                        </label>
                      </div>
                      <span className="text-xs text-slate-400">{cat.items.filter(i => generateForm.selected[i.key]).length}/{cat.items.length}</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {cat.items.map(item => {
                        const calculatedDate = item.daysFromDeath !== null ? addDays(generateForm.dateOfDeath, item.daysFromDeath) : null;
                        return (
                          <label key={item.key} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50/50 cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={!!generateForm.selected[item.key]}
                              onChange={() => toggleGenerateItem(item.key)}
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium text-slate-700">{item.label}</span>
                                {calculatedDate ? (
                                  <span className="text-xs text-slate-500 flex-shrink-0 tabular-nums">
                                    {formatDate(calculatedDate)}
                                    <span className="text-slate-400 ml-1">({item.daysFromDeath}d)</span>
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400 flex-shrink-0 italic">Date TBD</span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                              {item.notes && <p className="text-xs text-slate-400 mt-0.5 italic">{item.notes}</p>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          )}

          <div className="flex justify-between pt-4 border-t border-slate-100">
            <button onClick={() => setShowGenerateModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleGenerateDeadlines}
              disabled={!generateForm.dateOfDeath || selectedGenerateCount === 0 || saving}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Generating..." : `Generate ${selectedGenerateCount} Deadline${selectedGenerateCount !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Delete Confirmation ──────────────────────────────────────────────── */}
      <PremiumModal
        open={showDeleteConfirm !== null}
        onClose={() => setShowDeleteConfirm(null)}
        title="Delete Deadline"
        size="sm"
        icon={AlertTriangle}
      >
        <div className="p-4 text-center">
          <p className="text-sm text-slate-600 mb-4">
            Delete <strong>{showDeleteConfirm !== null ? deadlines[showDeleteConfirm]?.label : ""}</strong>? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
            <button onClick={() => showDeleteConfirm !== null && handleDelete(showDeleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700">Delete</button>
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default EstateDeadlinesTab;
